import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import Paywall from '../components/Paywall';
import { useSubscription } from '../lib/useSubscription';

const SECTIONS = [
  { key: 'contacts',  icon: '👥', label: 'Key Contacts',    route: '/app/contacts',  desc: 'Attorney, doctor, accountant' },
  { key: 'accounts',  icon: '🏦', label: 'Accounts',        route: '/app/accounts',  desc: 'Bank, investment, retirement' },
  { key: 'assets',    icon: '🏡', label: 'Assets',          route: '/app/assets',    desc: 'Real estate, vehicles' },
  { key: 'insurance', icon: '🛡️', label: 'Insurance',       route: '/app/insurance', desc: 'Life, health, home, auto' },
  { key: 'digital',   icon: '💻', label: 'Digital Life',    route: '/app/digital',   desc: 'Online accounts' },
  { key: 'documents', icon: '📄', label: 'Documents',       route: '/app/documents', desc: 'Wills, deeds, passports' },
  { key: 'wishes',    icon: '💌', label: 'Final Wishes',    route: '/app/wishes',    desc: 'Letters & preferences' },
  { key: 'guardians', icon: '🔐', label: 'Vault Guardians', route: '/app/guardians', desc: 'Access approval system' },
];

export default function Dashboard({ session }) {
  const [vault, setVault] = useState(null);
  const [profile, setProfile] = useState(null);
  const [counts, setCounts] = useState({});
  const [completion, setCompletion] = useState({});
  const [hoveredDrawer, setHoveredDrawer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [vaultOpen, setVaultOpen] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const { subscribed, refresh: refreshSubscription } = useSubscription(session);
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      const [{ data: profileData }, { data: vaultData }] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', session.user.id).single(),
        supabase.from('vaults').select('*').eq('owner_id', session.user.id).single(),
      ]);
      setProfile(profileData);
      setVault(vaultData);
      if (vaultData) {
        const [contacts, accounts, assets, insurance, digital, documents, wishes, guardians] = await Promise.all([
          supabase.from('contacts').select('id', { count: 'exact' }).eq('vault_id', vaultData.id),
          supabase.from('financial_accounts').select('id', { count: 'exact' }).eq('vault_id', vaultData.id),
          supabase.from('assets').select('id', { count: 'exact' }).eq('vault_id', vaultData.id),
          supabase.from('insurance_policies').select('id', { count: 'exact' }).eq('vault_id', vaultData.id),
          supabase.from('digital_accounts').select('id', { count: 'exact' }).eq('vault_id', vaultData.id),
          supabase.from('documents').select('id', { count: 'exact' }).eq('vault_id', vaultData.id),
          supabase.from('final_wishes').select('id', { count: 'exact' }).eq('vault_id', vaultData.id),
          supabase.from('vault_guardians').select('id', { count: 'exact' }).eq('vault_id', vaultData.id),
        ]);
        setCounts({
          contacts: contacts.count || 0, accounts: accounts.count || 0,
          assets: assets.count || 0, insurance: insurance.count || 0,
          digital: digital.count || 0, documents: documents.count || 0,
          wishes: wishes.count || 0, guardians: guardians.count || 0,
        });
        const { data: completionData } = await supabase
          .from('section_completion').select('*').eq('vault_id', vaultData.id);
        const completionMap = {};
        (completionData || []).forEach(r => { completionMap[r.section] = r.is_complete; });
        setCompletion(completionMap);
      }
      setLoading(false);
    };
    load();
  }, [session]);

  const handleUnlock = () => {
    if (subscribed === false) return;
    setUnlocking(true);
    setTimeout(() => { setVaultOpen(true); setUnlocking(false); }, 700);
  };

  const handleLock = () => setVaultOpen(false);

  const toggleComplete = async (e, sectionKey) => {
    e.stopPropagation();
    const isNowComplete = !completion[sectionKey];
    setCompletion(prev => ({ ...prev, [sectionKey]: isNowComplete }));
    await supabase.from('section_completion').upsert({
      vault_id: vault.id, owner_id: session.user.id,
      section: sectionKey, is_complete: isNowComplete,
      completed_at: isNowComplete ? new Date().toISOString() : null,
    }, { onConflict: 'vault_id,section' });
  };

  const completedCount = Object.values(completion).filter(Boolean).length;
  const completionPct = Math.round((completedCount / SECTIONS.length) * 100);
  const firstName = profile?.full_name?.split(' ')?.[0] || 'there';
  const serialNum = (vault?.id || '').slice(0, 8).toUpperCase();

  const style = `
    /* ── RESET SHELL PADDING FOR FULL VIEWPORT ── */
    .shell-main { padding: 0 !important; }

    /* ══════════════════════════════════════════
       FULL VIEWPORT VAULT
    ══════════════════════════════════════════ */
    .fv-wrap {
      width: 100%;
      height: calc(100vh - 58px); /* subtract topnav */
      position: relative;
      overflow: hidden;
      background: #111318;
    }

    /* ── VAULT EXTERIOR (fills entire viewport) ── */
    .fv-vault {
      position: absolute; inset: 0;
      background: linear-gradient(160deg, #3E434C 0%, #2A2E35 25%, #1E2128 55%, #2A2E35 80%, #3E434C 100%);
      display: flex; flex-direction: column;
    }

    /* brushed vertical texture */
    .fv-vault::before {
      content: '';
      position: absolute; inset: 0;
      background: repeating-linear-gradient(
        90deg,
        rgba(255,255,255,0.012) 0px, rgba(255,255,255,0.012) 1px,
        transparent 1px, transparent 3px
      );
      pointer-events: none; z-index: 0;
    }

    /* ── THICK SIDE WALLS ── */
    .fv-wall-left {
      position: absolute; top: 0; bottom: 0; left: 0; width: 52px;
      background: linear-gradient(90deg, #0D0F12, #1A1D22, #252930);
      box-shadow: inset -4px 0 20px rgba(0,0,0,0.5), 4px 0 0 rgba(255,255,255,0.04);
      z-index: 2;
    }
    .fv-wall-right {
      position: absolute; top: 0; bottom: 0; right: 0; width: 52px;
      background: linear-gradient(90deg, #252930, #1A1D22, #0D0F12);
      box-shadow: inset 4px 0 20px rgba(0,0,0,0.5), -4px 0 0 rgba(255,255,255,0.04);
      z-index: 2;
    }
    .fv-wall-top {
      position: absolute; top: 0; left: 0; right: 0; height: 36px;
      background: linear-gradient(180deg, #0D0F12, #1A1D22);
      box-shadow: inset 0 -4px 20px rgba(0,0,0,0.4), 0 4px 0 rgba(255,255,255,0.04);
      z-index: 2;
    }
    .fv-wall-bottom {
      position: absolute; bottom: 0; left: 0; right: 0; height: 36px;
      background: linear-gradient(180deg, #1A1D22, #0D0F12);
      box-shadow: inset 0 4px 20px rgba(0,0,0,0.4);
      z-index: 2;
    }

    /* ── RIVET STRIPS ── */
    .fv-rivets-h {
      position: absolute; left: 52px; right: 52px;
      display: flex; justify-content: space-between; align-items: center;
      padding: 0 20px; z-index: 3;
    }
    .fv-rivets-h.top { top: 10px; }
    .fv-rivets-h.bottom { bottom: 10px; }
    .fv-rivets-v {
      position: absolute; top: 36px; bottom: 36px;
      display: flex; flex-direction: column; justify-content: space-between;
      align-items: center; padding: 16px 0; z-index: 3;
    }
    .fv-rivets-v.left { left: 16px; }
    .fv-rivets-v.right { right: 16px; }
    .fv-rivet {
      width: 16px; height: 16px; border-radius: 50%; flex-shrink: 0;
      background: radial-gradient(circle at 35% 35%, #5A6370, #1A1D22);
      box-shadow: inset 0 2px 4px rgba(0,0,0,0.6), 0 1px 0 rgba(255,255,255,0.12);
    }

    /* ── INNER DOOR FRAME ── */
    .fv-door-frame {
      position: absolute;
      top: 36px; bottom: 36px; left: 52px; right: 52px;
      border: 6px solid #0D0F12;
      box-shadow:
        inset 0 0 60px rgba(0,0,0,0.5),
        0 0 0 1px rgba(255,255,255,0.06);
      z-index: 1;
      overflow: hidden;
      background: #1E2128;
    }

    /* ── CLOSED DOOR CONTENT ── */
    .fv-door-closed {
      position: absolute; inset: 0;
      display: flex; align-items: center;
      padding: 0 80px 0 100px;
      gap: 60px;
    }

    /* hinges */
    .fv-hinges {
      position: absolute; left: 36px; top: 0; bottom: 0;
      display: flex; flex-direction: column; justify-content: space-evenly;
      padding: 40px 0; z-index: 4;
    }
    .fv-hinge {
      width: 22px; height: 60px;
      background: linear-gradient(90deg, #6B7A86, #9DAAB5, #6B7A86);
      border-radius: 6px;
      box-shadow: inset 0 2px 0 rgba(255,255,255,0.3), 0 4px 10px rgba(0,0,0,0.6);
    }

    /* engraving */
    .fv-engraving {
      flex: 1;
      display: flex; flex-direction: column; gap: 10px;
    }
    .fv-engrave-eyebrow {
      font-size: clamp(11px, 1vw, 14px); font-weight: 700; letter-spacing: 0.2em;
      text-transform: uppercase; color: rgba(255,255,255,0.55);
      text-shadow: 0 1px 0 rgba(0,0,0,0.7);
      font-family: 'Plus Jakarta Sans', sans-serif;
    }
    .fv-engrave-name {
      font-family: 'Fraunces', serif; font-weight: 900;
      font-size: clamp(36px, 5vw, 72px);
      line-height: 1.0; color: rgba(255,255,255,0.92);
      text-shadow: 0 3px 10px rgba(0,0,0,0.5), 0 -1px 0 rgba(255,255,255,0.2);
    }
    .fv-engrave-name em {
      font-style: italic; color: #C4B5FD;
      text-shadow: 0 2px 16px rgba(139,92,246,0.5);
    }
    .fv-engrave-sub {
      font-size: clamp(13px, 1.2vw, 18px); color: rgba(255,255,255,0.5);
      font-family: 'Plus Jakarta Sans', sans-serif; font-weight: 400;
      margin-top: 6px; line-height: 1.6;
      text-shadow: 0 1px 0 rgba(0,0,0,0.5);
      max-width: 480px;
    }

    /* dial */
    .fv-dial-wrap {
      display: flex; flex-direction: column; align-items: center; gap: 14px;
      flex-shrink: 0;
    }
    .fv-dial {
      width: clamp(100px, 14vw, 180px);
      height: clamp(100px, 14vw, 180px);
      border-radius: 50%;
      background: radial-gradient(circle at 35% 30%, #4A4F58, #252930 50%, #111318 100%);
      border: 5px solid rgba(255,255,255,0.08);
      box-shadow:
        0 16px 48px rgba(0,0,0,0.7),
        inset 0 3px 0 rgba(255,255,255,0.12),
        inset 0 -3px 0 rgba(0,0,0,0.5);
      display: flex; align-items: center; justify-content: center;
      position: relative;
      animation: fvSpin 10s linear infinite;
    }
    @keyframes fvSpin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
    .fv-dial.fast { animation: fvSpin 0.3s linear infinite; }
    .fv-dial-mark {
      position: absolute; top: 10px; left: 50%; transform: translateX(-50%);
      width: 5px; height: 20px; background: rgba(255,255,255,0.5); border-radius: 3px;
    }
    .fv-dial-inner {
      width: 45%; height: 45%; border-radius: 50%;
      background: radial-gradient(circle at 35% 35%, #3E434C, #111318);
      border: 3px solid rgba(255,255,255,0.06);
    }
    .fv-dial-label {
      font-size: 11px; font-weight: 700; letter-spacing: 0.18em;
      color: rgba(255,255,255,0.3); text-transform: uppercase;
      font-family: 'Plus Jakarta Sans', sans-serif;
      text-shadow: 0 1px 0 rgba(0,0,0,0.6);
    }

    /* serial */
    .fv-serial {
      position: absolute; bottom: 16px; right: 20px;
      font-size: 10px; font-family: monospace; letter-spacing: 0.1em;
      color: rgba(255,255,255,0.15);
    }

    /* ── UNLOCK BUTTON (on door) ── */
    .fv-unlock-strip {
      position: absolute; bottom: 0; left: 0; right: 0; z-index: 5;
      display: flex; align-items: center; justify-content: center; gap: 12px;
      padding: 18px;
      background: linear-gradient(135deg, #8B5CF6, #38BDF8);
      border: none; cursor: pointer;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 700; font-size: clamp(15px, 1.5vw, 20px);
      color: #fff; letter-spacing: 0.04em;
      transition: opacity 0.2s;
      box-shadow: 0 -4px 24px rgba(139,92,246,0.35);
    }
    .fv-unlock-strip:hover { opacity: 0.92; }
    .fv-unlock-strip:disabled { opacity: 0.65; cursor: not-allowed; }

    /* ── OPEN STATE ── */
    .fv-open-wrap {
      position: absolute; inset: 0;
      perspective: 1200px;
    }

    /* swinging door panel */
    .fv-door-panel {
      position: absolute; top: 0; left: 0; bottom: 0; width: 52%;
      transform-origin: left center;
      transform-style: preserve-3d;
      transition: transform 1.4s cubic-bezier(0.4, 0, 0.2, 1);
      z-index: 10;
    }
    .fv-door-panel.swung {
      transform: perspective(1200px) rotateY(-115deg);
    }
    .fv-door-panel-face {
      position: absolute; inset: 0;
      background: linear-gradient(160deg, #4A4F58 0%, #32373F 40%, #252930 100%);
      border-right: 5px solid rgba(0,0,0,0.5);
      box-shadow: inset -6px 0 20px rgba(0,0,0,0.4);
    }
    .fv-door-panel-face::before {
      content: ''; position: absolute; inset: 20px;
      border: 2px solid rgba(255,255,255,0.06); border-radius: 6px;
    }
    .fv-panel-hinges {
      position: absolute; left: 14px; top: 0; bottom: 0;
      display: flex; flex-direction: column; justify-content: space-evenly; padding: 40px 0;
    }
    .fv-panel-hinge {
      width: 18px; height: 52px;
      background: linear-gradient(90deg, #6B7A86, #9DAAB5, #6B7A86);
      border-radius: 5px;
      box-shadow: inset 0 2px 0 rgba(255,255,255,0.3), 0 3px 8px rgba(0,0,0,0.5);
    }

    /* interior */
    .fv-interior {
      position: absolute; inset: 0;
      background: linear-gradient(180deg, #1C2333 0%, #161D2B 100%);
      display: flex; flex-direction: column;
      overflow: hidden;
    }
    .fv-interior::before {
      content: ''; position: absolute; top: 0; left: 0; bottom: 0; width: 28px;
      background: linear-gradient(90deg, rgba(0,0,0,0.5), transparent);
      pointer-events: none; z-index: 1;
    }
    .fv-interior::after {
      content: ''; position: absolute; top: 0; right: 0; bottom: 0; width: 28px;
      background: linear-gradient(270deg, rgba(0,0,0,0.5), transparent);
      pointer-events: none; z-index: 1;
    }

    /* interior top bar */
    .fv-int-bar {
      background: linear-gradient(90deg, #0D1117, #1a1f2e, #0D1117);
      padding: 12px 28px;
      display: flex; align-items: center; justify-content: space-between;
      border-bottom: 2px solid rgba(0,0,0,0.5); flex-shrink: 0;
    }
    .fv-int-bar-left { display: flex; align-items: center; gap: 10px; }
    .fv-light {
      width: 9px; height: 9px; border-radius: 50%;
      animation: fvBlink 2.5s ease-in-out infinite;
    }
    .fv-light.g { background: #22C55E; box-shadow: 0 0 8px #22C55E; }
    .fv-light.a { background: #F59E0B; box-shadow: 0 0 8px #F59E0B; animation-delay: 0.7s; }
    @keyframes fvBlink { 0%,100%{opacity:1} 50%{opacity:0.3} }
    .fv-int-title { font-size: 11px; font-weight: 700; letter-spacing: 0.14em; color: rgba(255,255,255,0.4); text-transform: uppercase; }
    .fv-int-bar-right { display: flex; align-items: center; gap: 14px; }
    .fv-prog-track { width: 120px; height: 4px; background: rgba(255,255,255,0.08); border-radius: 100px; overflow: hidden; }
    .fv-prog-fill { height: 100%; background: linear-gradient(90deg, #8B5CF6, #22C55E); border-radius: 100px; transition: width 0.8s ease; }
    .fv-prog-pct { font-size: 12px; font-weight: 700; color: #A78BFA; }
    .fv-guardian-pill {
      display: flex; align-items: center; gap: 5px;
      background: rgba(139,92,246,0.12); border: 1px solid rgba(139,92,246,0.2);
      border-radius: 100px; padding: 4px 12px;
      font-size: 11px; font-weight: 700; color: #A78BFA; cursor: pointer;
      transition: background 0.2s; white-space: nowrap;
    }
    .fv-guardian-pill:hover { background: rgba(139,92,246,0.22); }

    /* drawers container */
    .fv-drawers {
      flex: 1; overflow-y: auto;
      padding: 14px 24px;
      display: flex; flex-direction: column; gap: 6px;
    }
    .fv-drawers::-webkit-scrollbar { width: 4px; }
    .fv-drawers::-webkit-scrollbar-track { background: transparent; }
    .fv-drawers::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }

    /* drawer */
    .drw {
      cursor: pointer;
      transition: transform 0.25s cubic-bezier(0.4,0,0.2,1);
      flex-shrink: 0;
    }
    .drw:hover { transform: translateX(6px); }
    .drw-face {
      background: linear-gradient(135deg, #3D4F64 0%, #2A3A4A 60%, #243040 100%);
      border-radius: 8px;
      border: 1px solid rgba(255,255,255,0.08);
      border-bottom: 2px solid rgba(0,0,0,0.35);
      box-shadow: 0 3px 10px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.08);
      display: flex; align-items: center; overflow: hidden;
      transition: border-color 0.2s;
    }
    .drw:hover .drw-face { border-color: rgba(255,255,255,0.18); }
    .drw.complete .drw-face { background: linear-gradient(135deg, #1B3A2A 0%, #0F2218 100%); border-color: rgba(34,197,94,0.15); }
    .drw.complete:hover .drw-face { border-color: rgba(34,197,94,0.35); }

    .drw-strip { width: 5px; align-self: stretch; background: rgba(139,92,246,0.5); flex-shrink: 0; transition: background 0.2s; }
    .drw:hover .drw-strip { background: rgba(139,92,246,1); }
    .drw.complete .drw-strip { background: rgba(34,197,94,0.6); }
    .drw.complete:hover .drw-strip { background: rgba(34,197,94,1); }

    .drw-handle {
      width: 36px; height: 36px; border-radius: 8px; margin: 10px 12px;
      background: linear-gradient(145deg, #8E9EAB, #5D6D7E);
      border: 1px solid rgba(255,255,255,0.18);
      box-shadow: 0 3px 8px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.25);
      display: flex; align-items: center; justify-content: center;
      font-size: 16px; flex-shrink: 0; transition: transform 0.2s;
    }
    .drw:hover .drw-handle { transform: translateX(2px); }
    .drw.complete .drw-handle {
      background: linear-gradient(145deg, #22C55E, #16A34A);
      box-shadow: 0 3px 10px rgba(34,197,94,0.3), inset 0 1px 0 rgba(255,255,255,0.25);
    }

    .drw-info { flex: 1; padding: 10px 0; min-width: 0; }
    .drw-label { font-size: 14px; font-weight: 700; color: rgba(255,255,255,0.88); white-space: nowrap; transition: font-size 0.2s, color 0.2s; }
    .drw-desc { font-size: 11px; color: rgba(255,255,255,0.33); margin-top: 2px; transition: font-size 0.2s, color 0.2s; }
    .drw:hover .drw-label { font-size: 17px; color: #fff; }
    .drw:hover .drw-desc { font-size: 13px; color: rgba(255,255,255,0.6); }

    .drw-right { display: flex; align-items: center; gap: 8px; padding: 0 14px; flex-shrink: 0; }
    .drw-count { font-size: 10px; font-weight: 600; padding: 2px 8px; border-radius: 100px; background: rgba(255,255,255,0.06); color: rgba(255,255,255,0.35); white-space: nowrap; }
    .drw-count.has { background: rgba(139,92,246,0.18); color: #A78BFA; }
    .drw.complete .drw-count { background: rgba(34,197,94,0.12); color: #4ADE80; }
    .drw-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; transition: all 0.3s; }
    .drw-arrow { font-size: 14px; color: rgba(255,255,255,0.2); transition: color 0.2s, transform 0.2s; }
    .drw:hover .drw-arrow { color: rgba(255,255,255,0.7); transform: translateX(3px); }

    .drw-expand { max-height: 0; overflow: hidden; transition: max-height 0.3s ease, opacity 0.25s; opacity: 0; }
    .drw-expand.open { max-height: 72px; opacity: 1; }
    .drw-expand-inner {
      display: flex; align-items: center; justify-content: space-between; gap: 12px;
      padding: 10px 14px 10px 53px;
      background: rgba(0,0,0,0.22);
      border-top: 1px solid rgba(255,255,255,0.04);
      border-radius: 0 0 8px 8px;
    }
    .drw-msg { font-size: 12px; color: rgba(255,255,255,0.4); line-height: 1.4; }
    .drw-msg strong { color: rgba(255,255,255,0.65); }
    .drw-btns { display: flex; gap: 6px; flex-shrink: 0; }
    .drw-btn-go {
      background: linear-gradient(135deg, #8B5CF6, #38BDF8);
      color: #fff; border: none; border-radius: 100px;
      padding: 6px 14px; font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 600; font-size: 11px; cursor: pointer;
      box-shadow: 0 2px 8px rgba(139,92,246,0.3); transition: opacity 0.2s;
    }
    .drw-btn-go:hover { opacity: 0.85; }
    .drw-btn-done {
      background: transparent; border: 1px solid rgba(255,255,255,0.1);
      color: rgba(255,255,255,0.35); border-radius: 100px;
      padding: 5px 12px; font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 600; font-size: 11px; cursor: pointer; transition: all 0.2s;
    }
    .drw-btn-done:hover { border-color: rgba(34,197,94,0.5); color: #4ADE80; }
    .drw-btn-done.done { border-color: rgba(34,197,94,0.35); color: #4ADE80; background: rgba(34,197,94,0.07); }

    /* interior footer */
    .fv-int-footer {
      background: linear-gradient(90deg, #0D1117, #1a1f2e, #0D1117);
      padding: 10px 28px;
      display: flex; align-items: center; justify-content: space-between;
      border-top: 2px solid rgba(0,0,0,0.5); flex-shrink: 0;
    }
    .fv-int-footer-text { font-size: 9px; font-weight: 700; color: rgba(255,255,255,0.18); letter-spacing: 0.14em; text-transform: uppercase; }
    .fv-lock-btn {
      display: flex; align-items: center; gap: 6px;
      background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
      border-radius: 100px; padding: 5px 14px; cursor: pointer;
      font-size: 11px; font-weight: 600; color: rgba(255,255,255,0.35);
      font-family: 'Plus Jakarta Sans', sans-serif; transition: all 0.2s;
    }
    .fv-lock-btn:hover { background: rgba(239,68,68,0.08); border-color: rgba(239,68,68,0.2); color: #F87171; }

    /* stagger fade in */
    @keyframes fvFadeUp { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:none} }
    .drw { animation: fvFadeUp 0.4s ease both; }
    .drw:nth-child(1){animation-delay:0.8s} .drw:nth-child(2){animation-delay:0.95s}
    .drw:nth-child(3){animation-delay:1.1s} .drw:nth-child(4){animation-delay:1.25s}
    .drw:nth-child(5){animation-delay:1.4s} .drw:nth-child(6){animation-delay:1.55s}
    .drw:nth-child(7){animation-delay:1.7s} .drw:nth-child(8){animation-delay:1.85s}

    /* ── RESPONSIVE ── */
    @media(max-width:768px){
      .fv-wall-left,.fv-wall-right{width:28px}
      .fv-door-frame{left:28px;right:28px}
      .fv-rivets-h{left:28px;right:28px}
      .fv-rivets-v.left{left:6px} .fv-rivets-v.right{right:6px}
      .fv-rivet{width:10px;height:10px}
      .fv-door-closed{flex-direction:column;justify-content:center;padding:60px 40px 100px;gap:32px}
      .fv-hinges{display:none}
      .fv-engraving{text-align:center;align-items:center}
      .fv-engrave-sub{text-align:center}
      .fv-drawers{padding:10px 16px}
    }
    @media(max-width:480px){
      .fv-wall-left,.fv-wall-right{width:16px}
      .fv-wall-top,.fv-wall-bottom{height:20px}
      .fv-door-frame{top:20px;bottom:20px;left:16px;right:16px;border-width:3px}
    }
  `;

  if (loading) return (
    <div style={{
      height: 'calc(100vh - 58px)', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: '#1E2128', color: 'rgba(255,255,255,0.4)', fontSize: 14,
      fontFamily: 'Plus Jakarta Sans, sans-serif'
    }}>Loading your vault...</div>
  );

  const rivetsH = [...Array(14)].map((_,i) => <div key={i} className="fv-rivet" />);
  const rivetsV = [...Array(8)].map((_,i) => <div key={i} className="fv-rivet" />);

  return (
    <>
      <style>{style}</style>
      <div className="fv-wrap">
        <div className="fv-vault">

          {/* Thick walls */}
          <div className="fv-wall-left" />
          <div className="fv-wall-right" />
          <div className="fv-wall-top" />
          <div className="fv-wall-bottom" />

          {/* Rivets */}
          <div className="fv-rivets-h top">{rivetsH}</div>
          <div className="fv-rivets-h bottom">{rivetsH}</div>
          <div className="fv-rivets-v left">{rivetsV}</div>
          <div className="fv-rivets-v right">{rivetsV}</div>

          {/* Inner door frame */}
          <div className="fv-door-frame">

            {/* ── CLOSED ── */}
            {!vaultOpen && (
              <>
                <div className="fv-door-closed">
                  <div className="fv-hinges">
                    <div className="fv-hinge" /><div className="fv-hinge" /><div className="fv-hinge" />
                  </div>

                  <div className="fv-engraving">
                    <div className="fv-engrave-eyebrow">Your Legacy Vault</div>
                    <div className="fv-engrave-name">Welcome, <em>{firstName}.</em></div>
                    <div className="fv-engrave-sub">
                      Your family's most important information —<br />organized, protected, and ready.
                    </div>
                  </div>

                  <div className="fv-dial-wrap">
                    <div className={`fv-dial${unlocking ? ' fast' : ''}`}>
                      <div className="fv-dial-mark" />
                      <div className="fv-dial-inner" />
                    </div>
                    <div className="fv-dial-label">{unlocking ? 'Unlocking...' : 'Vault Sealed'}</div>
                  </div>

                  <div className="fv-serial">SN-{serialNum}</div>
                </div>

                <button className="fv-unlock-strip" onClick={handleUnlock} disabled={unlocking}>
                  🔓 &nbsp;{unlocking ? 'Unlocking Vault...' : 'Unlock My Vault'}
                </button>

                {/* PAYWALL — shown when not subscribed */}
                {subscribed === false && (
                  <Paywall session={session} onSubscribed={() => { refreshSubscription(); }} />
                )}
              </>
            )}

            {/* ── OPEN ── */}
            {vaultOpen && (
              <div className="fv-open-wrap">
                <div className={`fv-door-panel${vaultOpen ? ' swung' : ''}`}>
                  <div className="fv-door-panel-face">
                    <div className="fv-panel-hinges">
                      <div className="fv-panel-hinge" /><div className="fv-panel-hinge" /><div className="fv-panel-hinge" />
                    </div>
                  </div>
                </div>

                <div className="fv-interior">
                  <div className="fv-int-bar">
                    <div className="fv-int-bar-left">
                      <div className="fv-light g" /><div className="fv-light a" />
                      <div className="fv-int-title">{vault?.name || 'My Legacy Vault'}</div>
                    </div>
                    <div className="fv-int-bar-right">
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <div className="fv-prog-track">
                          <div className="fv-prog-fill" style={{width:`${completionPct}%`}} />
                        </div>
                        <div className="fv-prog-pct">{completionPct}%</div>
                      </div>
                      <div className="fv-guardian-pill" onClick={() => navigate('/app/guardians')}>
                        🛡️ {vault?.guardian_threshold||3} of {vault?.guardian_count||3} Guardians
                      </div>
                    </div>
                  </div>

                  <div className="fv-drawers">
                    {SECTIONS.map((s) => {
                      const isComplete = completion[s.key];
                      const count = counts[s.key] || 0;
                      const isHovered = hoveredDrawer === s.key;
                      const hasItems = count > 0;
                      const dotColor = isComplete ? '#22C55E' : hasItems ? '#F59E0B' : 'rgba(255,255,255,0.15)';
                      const dotGlow = isComplete ? '0 0 6px #22C55E' : hasItems ? '0 0 6px #F59E0B' : 'none';

                      return (
                        <div key={s.key}
                          className={`drw${isComplete?' complete':''}`}
                          onMouseEnter={() => setHoveredDrawer(s.key)}
                          onMouseLeave={() => setHoveredDrawer(null)}
                          onClick={() => navigate(s.route)}
                        >
                          <div className="drw-face">
                            <div className="drw-strip" />
                            <div className="drw-handle">{s.icon}</div>
                            <div className="drw-info">
                              <div className="drw-label">{s.label}</div>
                              <div className="drw-desc">{s.desc}</div>
                            </div>
                            <div className="drw-right">
                              <div className={`drw-count${hasItems?' has':''}`}>
                                {count===0?'Empty':`${count} item${count!==1?'s':''}`}
                              </div>
                              <div className="drw-dot" style={{background:dotColor,boxShadow:dotGlow}} />
                              <div className="drw-arrow">›</div>
                            </div>
                          </div>
                          <div className={`drw-expand${isHovered?' open':''}`}>
                            <div className="drw-expand-inner">
                              <div className="drw-msg">
                                {isComplete
                                  ? <><strong>✓ Complete.</strong> Your family can find this.</>
                                  : count===0
                                    ? <><strong>Nothing added yet.</strong> Your family will need this.</>
                                    : <><strong>{count} item{count!==1?'s':''} added.</strong> Mark done when satisfied.</>
                                }
                              </div>
                              <div className="drw-btns">
                                <button className="drw-btn-go" onClick={e=>{e.stopPropagation();navigate(s.route);}}>Open →</button>
                                <button className={`drw-btn-done${isComplete?' done':''}`} onClick={e=>toggleComplete(e,s.key)}>
                                  {isComplete?'✓ Done':'Mark done'}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="fv-int-footer">
                    <div className="fv-int-footer-text">Merq Legacy · 3-Guardian Active · SN-{serialNum}</div>
                    <button className="fv-lock-btn" onClick={handleLock}>🔒 Lock vault</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
